/**
 * Iter122k95 / Iter122k110 · Atto di Liquidazione (Verbale di Accordo) — allegazione + editor.
 *
 * Flow:
 * 1. L'utente allega il PDF ricevuto dalla compagnia/perito (via editor).
 * 2. Apre l'editor "Conferma preventivo" in modalità `contesto=sinistro` con
 *    `sinistro_id` valorizzato: da lì carica il PDF, ci sovrappone i campi
 *    (dati condominio, danneggiati, IBAN, note, luogo/data, firma) e trasmette.
 *
 * Iter122k110 · Dopo l'invio dell'editor la card cambia stato:
 * - il badge/pulsante diventa VERDE "Atto di liquidazione sottoscritto"
 * - la descrizione scompare
 * - si mostra l'elenco "Atto di liquidazione trasmesso in data DD/MM/YYYY HH:MM"
 * - compare il comando "Reinvia atto" con due opzioni:
 *     a) reinvia lo stesso atto (nuova email al destinatario indicato)
 *     b) compila una nuova versione (apre editor)
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, openSecureFile } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { toast } from "sonner";
import { FileText, PenTool, Download, Send, Check, RefreshCw, CheckCircle2, Trash2 } from "lucide-react";

const statoLabel = (s) => ({
  bozza: "Bozza", generata: "Generata", inviata: "Inviato", inviato: "Inviato", firmato: "Firmato",
}[s] || s || "Bozza");
const statoStyle = (s) => ({
  bozza: "bg-amber-100 text-amber-800 border-amber-300",
  generata: "bg-sky-100 text-sky-800 border-sky-300",
  inviata: "bg-emerald-100 text-emerald-800 border-emerald-300",
  inviato: "bg-emerald-100 text-emerald-800 border-emerald-300",
  firmato: "bg-emerald-100 text-emerald-800 border-emerald-300",
}[s] || "bg-slate-100 text-slate-700 border-slate-300");

const isSent = (c) => c && (c.stato === "inviata" || c.stato === "inviato" || c.stato === "firmato" || (c.invii_history || []).length > 0);

const fmtDataInvio = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const getInvii = (c) => {
  const arr = Array.isArray(c.invii_history) ? [...c.invii_history] : [];
  // Retro-compat: se non c'è invii_history ma la conferma risulta inviata,
  // ricostruisci una voce sintetica dai campi legacy `inviata_at` / `inviata_a`.
  if (arr.length === 0 && (c.stato === "inviata" || c.stato === "inviato") && c.inviata_at) {
    arr.push({
      data: c.inviata_at,
      destinatari: (c.inviata_a || "").split(",").map((s) => s.trim()).filter(Boolean),
      file_id: c.signed_file_id || null,
    });
  }
  return arr;
};

export function AttoLiquidazioneCard({ sinistroId, form }) {
  const navigate = useNavigate();
  const [conferme, setConferme] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  // Iter123k258 · Il dialog ora gestisce 3 modalità: primo invio di una
  // bozza, re-invio di una conferma già spedita, invio multiplo (tutte le
  // bozze del sinistro). `mode` ∈ {"invio","reinvio","multi"}; su "multi"
  // `cid` resta null e `cids[]` contiene le bozze da inviare.
  const [reinviaDlg, setReinviaDlg] = useState({
    open: false, cid: null, cids: [], mode: "invio",
    emails: "", messaggio: "",
  });
  const [reinviaBusy, setReinviaBusy] = useState(false);

  useEffect(() => {
    if (!sinistroId) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    api.get(`/conferme?sinistro_id=${sinistroId}`)
      .then((r) => { if (alive) setConferme(r.data?.conferme || []); })
      .catch(() => { if (alive) setConferme([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sinistroId, reload]);

  const apriEditor = (cid) => {
    const q = new URLSearchParams();
    q.set("contesto", "sinistro");
    if (sinistroId) q.set("sinistro_id", sinistroId);
    if (form?.condominio_id) q.set("condominio_id", form.condominio_id);
    if (cid) navigate(`/conferma-preventivo/${cid}?${q.toString()}`);
    else navigate(`/conferma-preventivo?${q.toString()}`);
  };

  const scaricaFinale = (c) => {
    const fid = c.output_file_id || c.signed_file_id || c.source_file_id;
    if (!fid) return;
    // Iter123k193 · Prefisso `/api` è aggiunto automaticamente da secureFileUrl: NON passarlo
    // qui (prima generava `/api/api/files/...` → 400 "Risorsa non valida").
    openSecureFile(`/files/${fid}`);
  };

  // Iter123k258 · Apertura dialog di invio in base al contesto.
  const emailsSuggerite = () => {
    // Pre-compila il dialog con destinatari sensati del sinistro: dato che
    // il context qui non espone ancora la mail del perito/compagnia,
    // partiamo da campo vuoto; l'operatore può incollare quelle salvate
    // nella pratica.
    return "";
  };

  const openInviaDlg = (c) => {
    const ultimoInvio = getInvii(c).slice(-1)[0];
    const emailsIniziali = (ultimoInvio?.destinatari || []).join(", ")
      || c.inviata_a || emailsSuggerite();
    const mode = isSent(c) ? "reinvio" : "invio";
    setReinviaDlg({ open: true, cid: c.id, cids: [], mode,
                      emails: emailsIniziali, messaggio: "" });
  };

  // Alias retro-compatibile: openReinviaDlg → openInviaDlg
  const openReinviaDlg = openInviaDlg;

  const openInviaTuttiDlg = () => {
    const nonInviate = conferme.filter((x) => !isSent(x));
    if (!nonInviate.length) {
      toast.error("Nessuna bozza da inviare"); return;
    }
    setReinviaDlg({
      open: true, cid: null, mode: "multi",
      cids: nonInviate.map((x) => x.id),
      emails: emailsSuggerite(),
      messaggio: "",
    });
  };

  const confermaReinvia = async () => {
    const emails = reinviaDlg.emails.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (emails.length === 0) { toast.error("Indicare almeno un destinatario"); return; }
    setReinviaBusy(true);
    try {
      if (reinviaDlg.mode === "multi") {
        // Invio sequenziale per rispettare rate limit SMTP del tenant.
        let ok = 0; let errs = 0;
        for (const cid of reinviaDlg.cids) {
          try {
            await api.post(`/conferme/${cid}/invia`,
              { emails, messaggio: reinviaDlg.messaggio });
            ok += 1;
          } catch (_e) { errs += 1; }
        }
        if (ok > 0) toast.success(`Inviate ${ok} bozza/e a ${emails.length} destinatario/i`);
        if (errs > 0) toast.error(`${errs} invio/i non riuscito/i`);
      } else {
        await api.post(`/conferme/${reinviaDlg.cid}/invia`,
          { emails, messaggio: reinviaDlg.messaggio });
        toast.success(reinviaDlg.mode === "reinvio"
          ? `Atto reinviato a ${emails.length} destinatario/i`
          : `Atto inviato a ${emails.length} destinatario/i`);
      }
      setReinviaDlg({ open: false, cid: null, cids: [], mode: "invio",
                        emails: "", messaggio: "" });
      setReload((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'invio");
    } finally {
      setReinviaBusy(false);
    }
  };

  // Iter123k257 · cancella una bozza allegata per errore. Non consentito per
  // le conferme già inviate (si vede comunque il tasto Reinvia).
  const eliminaConferma = async (c) => {
    const nome = c.numero || c.source_filename || c.id.slice(0, 8);
    if (!window.confirm(`Eliminare la bozza "${nome}"?\n\nIl PDF allegato sarà rimosso e non potrà essere recuperato.`)) return;
    try {
      await api.delete(`/conferme/${c.id}`);
      toast.success("Bozza eliminata");
      setReload((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const attoSottoscritto = conferme.some(isSent);

  return (
    <div className="border-2 border-amber-200 rounded-lg p-4 bg-amber-50/30 space-y-3" data-testid="atto-liquidazione-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-bold uppercase tracking-wide text-amber-800 flex items-center gap-2">
          <FileText className="w-4 h-4" /> Atto di Liquidazione (Verbale di Accordo)
        </div>
        {sinistroId && !attoSottoscritto && (
          <Button type="button" size="sm" onClick={() => apriEditor(null)}
            data-testid="atto-nuovo-editor" className="h-9 bg-amber-700 hover:bg-amber-800 text-white">
            <PenTool className="w-4 h-4 mr-1" /> Allega/Compila atto ricevuto
          </Button>
        )}
        {sinistroId && attoSottoscritto && (
          <div className="flex items-center gap-2 flex-wrap">
            <div data-testid="atto-sottoscritto-badge"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 text-white text-sm font-bold shadow-sm">
              <CheckCircle2 className="w-4 h-4" /> Atto di liquidazione sottoscritto
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" size="sm" variant="outline"
                  data-testid="atto-reinvia-menu"
                  className="h-9 border-amber-400 text-amber-800 hover:bg-amber-50">
                  <RefreshCw className="w-4 h-4 mr-1" /> Reinvia atto
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem
                  data-testid="atto-reinvia-stesso"
                  onClick={() => {
                    const c = conferme.find(isSent);
                    if (c) openReinviaDlg(c);
                  }}>
                  <Send className="w-4 h-4 mr-2" /> Reinvia lo stesso atto
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="atto-reinvia-nuovo"
                  onClick={() => {
                    const c = conferme.find(isSent);
                    apriEditor(c ? c.id : null);
                  }}>
                  <PenTool className="w-4 h-4 mr-2" /> Compila una nuova versione
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {!attoSottoscritto && (
        <p className="text-xs text-slate-600" data-testid="atto-descrizione">
          Allega qui il PDF dell'atto di liquidazione ricevuto dalla compagnia o dal perito
          e completalo con l'editor: potrai sovrapporre dati del condominio, danneggiati, IBAN,
          note, luogo/data e firma. Al termine invii l'atto sottoscritto ad agenzia, perito o
          liquidatore direttamente dall'editor.
        </p>
      )}

      {!sinistroId && (
        <div className="text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-lg p-3">
          Salva prima la pratica per abilitare l'editor dell'atto di liquidazione.
        </div>
      )}

      {sinistroId && !loading && conferme.length === 0 && (
        <div className="text-sm text-slate-500 border-2 border-dashed border-amber-200 rounded-lg p-3"
          data-testid="atto-nessuna-bozza">
          Nessun atto ancora allegato. Clicca <b>«Allega/Compila atto ricevuto»</b> per iniziare.
        </div>
      )}

      {sinistroId && attoSottoscritto && (
        <div className="space-y-1 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-3"
          data-testid="atto-trasmissioni">
          {conferme.filter(isSent).flatMap((c) => getInvii(c).map((inv, idx) => (
            <div key={`${c.id}-${idx}`} className="flex items-center gap-2 flex-wrap"
              data-testid={`atto-trasmesso-${c.id}-${idx}`}>
              <Check className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
              <span>
                Atto di liquidazione trasmesso in data <b>{fmtDataInvio(inv.data)}</b>
                {inv.destinatari?.length > 0 && (
                  <> a <span className="font-medium">{inv.destinatari.join(", ")}</span></>
                )}
              </span>
            </div>
          )))}
        </div>
      )}

      {sinistroId && conferme.length > 0 && (
        <div className="space-y-2" data-testid="atto-conferme-list">
          {conferme.map((c) => (
            <div key={c.id} data-testid={`atto-conferma-${c.id}`}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 bg-white border-2 border-slate-200 rounded-lg p-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold text-slate-900 break-all">{c.numero || `Bozza ${c.id.slice(0, 8)}`}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statoStyle(c.stato)}`}>
                    {statoLabel(c.stato).toUpperCase()}
                  </span>
                  {!isSent(c) && (
                    <span className="text-[10px] font-semibold text-amber-700 inline-flex items-center gap-1">
                      <Send className="w-3 h-3" /> Da inviare
                    </span>
                  )}
                </div>
                {c.source_filename && (
                  <div data-testid={`atto-filename-${c.id}`}
                    className="text-xs text-slate-700 mt-0.5 flex items-center gap-1 min-w-0">
                    <FileText className="w-3 h-3 shrink-0 text-slate-400" />
                    <span className="truncate">{c.source_filename}</span>
                  </div>
                )}
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {c.updated_at ? new Date(c.updated_at).toLocaleString("it-IT") : ""}
                  {c.creato_da_nome ? ` · ${c.creato_da_nome}` : ""}
                </div>
              </div>
              <div className="flex gap-2 shrink-0 flex-wrap sm:flex-nowrap">
                <Button type="button" size="sm" variant="outline"
                  data-testid={`atto-open-${c.id}`}
                  onClick={() => apriEditor(c.id)}
                  className="h-9 border-indigo-300 text-indigo-800 hover:bg-indigo-50">
                  <PenTool className="w-4 h-4 mr-1" /> Apri editor
                </Button>
                {(c.output_file_id || c.signed_file_id || c.source_file_id) && (
                  <Button type="button" size="sm" variant="outline"
                    data-testid={`atto-download-${c.id}`}
                    onClick={() => scaricaFinale(c)}
                    className="h-9">
                    <Download className="w-4 h-4 mr-1" /> PDF
                  </Button>
                )}
                {/* Iter123k258 · Invio singolo per ciascun atto in bozza */}
                {!isSent(c) && (
                  <Button type="button" size="sm"
                    data-testid={`atto-invia-${c.id}`}
                    onClick={() => openInviaDlg(c)}
                    className="h-9 bg-amber-600 hover:bg-amber-700 text-white">
                    <Send className="w-4 h-4 mr-1" /> Invia
                  </Button>
                )}
                {isSent(c) && (
                  <Button type="button" size="sm" variant="outline"
                    data-testid={`atto-reinvia-${c.id}`}
                    onClick={() => openInviaDlg(c)}
                    title="Reinvia l'atto"
                    className="h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                )}
                {!isSent(c) && (
                  <Button type="button" size="sm" variant="outline"
                    data-testid={`atto-delete-${c.id}`}
                    onClick={() => eliminaConferma(c)}
                    title="Elimina bozza"
                    className="h-9 border-red-300 text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
          {/* Iter123k258 · Pulsante Invia tutti (prima della sezione Note). */}
          {conferme.some((x) => !isSent(x)) && (
            <div className="pt-2 flex justify-end">
              <Button type="button"
                data-testid="atto-invia-tutti"
                onClick={openInviaTuttiDlg}
                className="h-10 bg-amber-700 hover:bg-amber-800 text-white font-bold shadow-sm">
                <Send className="w-4 h-4 mr-2" />
                Invia tutti ({conferme.filter((x) => !isSent(x)).length})
              </Button>
            </div>
          )}
        </div>
      )}

      <Dialog open={reinviaDlg.open} onOpenChange={(o) => !o && setReinviaDlg({ open: false, cid: null, cids: [], mode: "invio", emails: "", messaggio: "" })}>
        <DialogContent data-testid="atto-reinvia-dialog">
          <DialogHeader>
            <DialogTitle>
              {reinviaDlg.mode === "multi"
                ? `Invia ${reinviaDlg.cids.length} atti di liquidazione`
                : reinviaDlg.mode === "reinvio"
                  ? "Reinvia atto di liquidazione"
                  : "Invia atto di liquidazione"}
            </DialogTitle>
            <DialogDescription>
              {reinviaDlg.mode === "multi"
                ? "Tutti gli atti in bozza verranno inviati agli stessi destinatari con il medesimo messaggio."
                : reinviaDlg.mode === "reinvio"
                  ? "Invia nuovamente lo stesso atto sottoscritto ai destinatari indicati."
                  : "Invia l'atto sottoscritto ad agenzia, perito o liquidatore."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-semibold">Destinatari email</Label>
              <Input
                data-testid="atto-reinvia-emails"
                value={reinviaDlg.emails}
                onChange={(e) => setReinviaDlg((d) => ({ ...d, emails: e.target.value }))}
                placeholder="agenzia@example.it, perito@example.it"
                className="mt-1"
              />
              <div className="text-xs text-slate-500 mt-1">Separati da virgola, punto e virgola o spazio.</div>
            </div>
            <div>
              <Label className="text-sm font-semibold">Messaggio (opzionale)</Label>
              <Textarea
                data-testid="atto-reinvia-messaggio"
                value={reinviaDlg.messaggio}
                onChange={(e) => setReinviaDlg((d) => ({ ...d, messaggio: e.target.value }))}
                placeholder="Lascia vuoto per usare il testo standard"
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline"
              data-testid="atto-reinvia-annulla"
              onClick={() => setReinviaDlg({ open: false, cid: null, cids: [], mode: "invio", emails: "", messaggio: "" })}>
              Annulla
            </Button>
            <Button type="button"
              data-testid="atto-reinvia-conferma"
              disabled={reinviaBusy}
              onClick={confermaReinvia}
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Send className="w-4 h-4 mr-1" />{" "}
              {reinviaBusy
                ? "Invio…"
                : reinviaDlg.mode === "multi"
                  ? `Invia tutti (${reinviaDlg.cids.length})`
                  : reinviaDlg.mode === "reinvio" ? "Reinvia" : "Invia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
